import React, { useState, useCallback, useEffect } from 'react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import type { Language } from '../../../../../config/localization';
import { resolveMaskPayloadKey, MASK_PAYLOAD_KEYS_MARKER, RESERVED_COUNTER_PAYLOAD_KEYS } from '../../../../ui/modals/maskGeometry';
import { MODEL_EXTENSIONS } from '../../../../../utils/mediaTypeDetection';
import {
  normalizeVideoSlotConstraints,
  type VideoSlotConstraints,
} from '../../../../../utils/slotVideoConstraints';

// Slot configuration types (must match BigSlotsInputMediaWidget)
type MediaType = 'image' | 'video' | 'audio' | 'model';

export interface SlotConfig extends VideoSlotConstraints {
  slot_id: string;
  nameRu: string;
  nameEn: string;
  mediaType: MediaType;
  required: boolean;
  maxCount: number;
  // Accepted file formats (e.g. ['.mp4']). Empty/undefined = all defaults for the media type.
  acceptedFormats?: string[];
  // Image restrictions
  imageMaxFileSize?: number;
  imageMinWidth?: number;
  imageMaxWidth?: number;
  imageMinHeight?: number;
  imageMaxHeight?: number;
  // Audio restrictions
  audioMaxFileSize?: number;
  audioMaxDuration?: number;
  // 3D model restrictions
  modelMaxFileSize?: number;
  // Target dimensions (center crop + resize)
  targetWidth?: number;
  targetHeight?: number;
  // JPEG conversion (flatten alpha to white background)
  convertToJpeg?: boolean;
  // Inpainting mask (image slots; on multi-image slots the mask binds to the first image)
  enableMask?: boolean;
  maskPayloadKey?: string;
}

const DEFAULT_FORMATS: Record<MediaType, string[]> = {
  image: ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
  video: ['.mp4', '.webm', '.mov', '.avi'],
  audio: ['.mp3', '.wav', '.ogg', '.flac', '.m4a'],
  model: MODEL_EXTENSIONS.map(extension => `.${extension}`),
};

export interface SlotsInputMediaConfig {
  slots: SlotConfig[];
}

export interface WorkflowWidget {
  id: number;
  workflow_id: number;
  widget_type_id: number;
  is_enabled: boolean;
  display_order: number;
  config_data?: string;
  widget_type: {
    id: number;
    name: string;
    description: string;
    icon?: string;
  };
}

export interface SlotsInputMediaSectionProps {
  workflowWidgets: WorkflowWidget[];
  onWidgetConfigChange: (widgetTypeId: number, configData: string) => void;
  language: Language;
  /**
   * The workflow's input-files batching mode. Masking is only safe on
   * `all_per_request` — under `single_per_request` each requiredFiles entry spawns a
   * separate, separately-charged request, so the mask PNG would become its own billed
   * generation. The mask UI is gated off (and stale config cleared) when this is
   * `single_per_request`. Undefined defaults to `all_per_request` (mask allowed).
   */
  inputFilesLogic?: string;
}

// Masking only works on all_per_request workflows (see prop docs). Treat undefined as
// all_per_request (the workflow default).
export function maskAllowedForInputFilesLogic(inputFilesLogic?: string): boolean {
  return inputFilesLogic !== 'single_per_request';
}

// Save-time normalization: strip inpainting-mask config from a slots-widget config blob
// when the workflow is single_per_request. The in-section useEffect that clears stale
// mask config only runs while THIS section is mounted — an admin can flip
// input_files_logic on the General tab and save without ever opening the Slots tab, which
// would otherwise persist `enableMask` alongside single_per_request and let the runtime
// widget emit a mask File that single_per_request bills as its own request. This runs at
// the single save chokepoint (useWorkflowEditor), so persisted config stays consistent
// regardless of which section was open. Returns the original value when nothing changed
// (unparseable JSON, no slots, or no stale mask) so unrelated widget configs pass through.
export function stripMaskConfigForInputFilesLogic(
  configData: string | null | undefined,
  inputFilesLogic?: string
): string | null | undefined {
  if (!configData || maskAllowedForInputFilesLogic(inputFilesLogic)) return configData;
  let parsed: any;
  try {
    parsed = JSON.parse(configData);
  } catch {
    return configData;
  }
  if (!parsed || !Array.isArray(parsed.slots)) return configData;
  const hasStaleMask = parsed.slots.some((s: any) => s?.enableMask || s?.maskPayloadKey);
  if (!hasStaleMask) return configData;
  const slots = parsed.slots.map((s: any) =>
    s?.enableMask || s?.maskPayloadKey
      ? { ...s, enableMask: undefined, maskPayloadKey: undefined }
      : s
  );
  return JSON.stringify({ ...parsed, slots });
}

// Generate unique slot ID
function generateSlotId(): string {
  return `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

const RESERVED_MEDIA_SLOT_IDS = new Set(['image_urls', 'video_urls', 'audio_urls']);

export function slotIdCollidesWithReservedMediaKey(slotId: string): boolean {
  return RESERVED_MEDIA_SLOT_IDS.has(slotId);
}

// Mask editor is meaningful for any image slot. For multi-image slots the single mask binds to
// the slot's FIRST image (image_urls[0] — the only image gpt-image-2 edit masks); later images
// are crop-only. `maxCount` is intentionally not constrained here.
export function slotSupportsMask(slot: Pick<SlotConfig, 'mediaType' | 'maxCount'>): boolean {
  return slot.mediaType === 'image';
}

// Flags a blank mask payload key (enabled but key cleared) — drives the inline
// red-border nudge prompting the admin to set an explicit key. The key is defaulted to
// 'mask_image' on enable, so this only fires if an admin deliberately clears it. It does
// NOT block save and is NOT data loss: the runtime falls back to 'mask_image' for a blank
// key (see getPayloadData), so a painted mask is never silently dropped.
export function maskPayloadKeyHasError(slot: Pick<SlotConfig, 'enableMask' | 'maskPayloadKey'>): boolean {
  return !!slot.enableMask && !(slot.maskPayloadKey && slot.maskPayloadKey.trim());
}

// Flags a mask payload key that collides with another field in the runtime payload — the
// reserved marker key, a media counter key (slots_image_count, video_width, …), or any
// slot's slot_id (its own or another's). At runtime such a key overwrites / is overwritten
// by that entry while the mask File still rides in requiredFiles, breaking the ordering
// invariant — or, for a counter key, the mask is silently dropped because getPayloadData
// skips a key already in data. The runtime validateState guard blocks submit on it; this is
// the display-only admin nudge mirroring it, and does NOT block save (mirrors
// maskPayloadKeyHasError). `slots` is the full slot list so cross-slot collisions surface.
export function maskPayloadKeyCollides(slot: SlotConfig, slots: SlotConfig[]): boolean {
  if (!slot.enableMask) return false;
  const effectiveKey = resolveMaskPayloadKey(slot.maskPayloadKey);
  if (effectiveKey === MASK_PAYLOAD_KEYS_MARKER) return true;
  if (RESERVED_COUNTER_PAYLOAD_KEYS.includes(effectiveKey)) return true;
  return slots.some(s => !!s.slot_id && s.slot_id === effectiveKey);
}

/**
 * Keep persisted model slots limited to common slot fields plus model restrictions.
 * This also strips the model-only limit from every other media type, including when
 * an admin changes a model slot back to image, video, or audio.
 */
export function normalizeSlotForSave(slot: SlotConfig): SlotConfig {
  if (slot.mediaType !== 'model') {
    const normalized = slot.modelMaxFileSize === undefined
      ? { ...slot }
      : { ...slot, modelMaxFileSize: undefined };
    if (slot.mediaType === 'video') {
      Object.assign(normalized, normalizeVideoSlotConstraints(slot));
    }
    return normalized;
  }

  // Preserve common/future fields without maintaining a model-field whitelist, while
  // removing restrictions that belong only to the three legacy media types.
  const normalized = { ...slot };
  const nonModelFields: Array<keyof SlotConfig> = [
    'imageMaxFileSize', 'imageMinWidth', 'imageMaxWidth', 'imageMinHeight', 'imageMaxHeight',
    'videoMaxFileSize', 'videoMinDuration', 'videoMaxDuration', 'videoMinWidth', 'videoMaxWidth',
    'videoMinHeight', 'videoMaxHeight', 'videoMinPixels', 'videoMaxPixels',
    'audioMaxFileSize', 'audioMaxDuration',
    'targetWidth', 'targetHeight', 'convertToJpeg', 'enableMask', 'maskPayloadKey',
  ];
  nonModelFields.forEach(field => delete normalized[field]);
  return normalized;
}

export function changeSlotMediaType(slot: SlotConfig, mediaType: MediaType): SlotConfig {
  return normalizeSlotForSave({
    ...slot,
    mediaType,
    acceptedFormats: undefined,
    ...(mediaType !== 'image' ? { enableMask: undefined, maskPayloadKey: undefined } : {}),
    ...(mediaType !== 'video' ? {
      videoMinDuration: undefined,
      videoMinPixels: undefined,
      videoMaxPixels: undefined,
    } : {}),
    ...(mediaType !== 'model' ? { modelMaxFileSize: undefined } : {}),
  });
}

export function canConfigureModelSlot(widgetTypeName?: string | null): boolean {
  return widgetTypeName === 'Slots Input Media';
}

// Create default slot
function createDefaultSlot(): SlotConfig {
  return {
    slot_id: generateSlotId(),
    nameRu: '',
    nameEn: '',
    mediaType: 'image',
    required: false,
    maxCount: 1,
    imageMaxFileSize: 20,
    imageMinWidth: undefined,
    imageMaxWidth: undefined,
    imageMinHeight: undefined,
    imageMaxHeight: undefined,
    videoMaxFileSize: 100,
    videoMinDuration: undefined,
    videoMaxDuration: undefined,
    videoMinPixels: undefined,
    videoMaxPixels: undefined,
    audioMaxFileSize: 50,
    audioMaxDuration: undefined
  };
}

export default function SlotsInputMediaSection({
  workflowWidgets,
  onWidgetConfigChange,
  language,
  inputFilesLogic
}: SlotsInputMediaSectionProps) {
  const t = useLocalization(language);
  const maskAllowed = maskAllowedForInputFilesLogic(inputFilesLogic);
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Find the Slots Input Media widget (prefer Big, fallback to Small)
  const slotsWidget =
    workflowWidgets.find(w => w.widget_type?.name === 'Slots Input Media' && w.is_enabled) ||
    workflowWidgets.find(w => w.widget_type?.name === 'Slots Input Media Small' && w.is_enabled);

  const hasWidget = !!slotsWidget;

  // Masking is implemented only in the Big slots runtime widget (BigSlotsInputMediaWidget).
  // This section falls back to the Small widget when Big is absent, but the Small runtime
  // has no mask button / payload assembly — exposing the mask config there would let an
  // admin save settings that silently no-op at runtime. Gate the mask UI to the Big widget.
  // (Small port is a known follow-up — see CLAUDE.md "Slots Input Media Widgets".)
  const maskSupportedByWidget = slotsWidget?.widget_type?.name === 'Slots Input Media';
  const modelSupportedByWidget = canConfigureModelSlot(slotsWidget?.widget_type?.name);

  // Parse current config
  let config: SlotsInputMediaConfig = { slots: [] };
  if (slotsWidget?.config_data) {
    try {
      config = JSON.parse(slotsWidget.config_data);
    } catch (e) {
      console.warn('Failed to parse Slots Input Media config:', e);
    }
  }

  const slots = config.slots || [];

  // Save config
  const saveConfig = useCallback((newSlots: SlotConfig[]) => {
    if (!slotsWidget) return;
    const newConfig: SlotsInputMediaConfig = { slots: newSlots.map(normalizeSlotForSave) };
    onWidgetConfigChange(slotsWidget.widget_type_id, JSON.stringify(newConfig));
  }, [slotsWidget, onWidgetConfigChange]);

  // Add slot
  const addSlot = useCallback(() => {
    const newSlot = createDefaultSlot();
    const newIndex = slots.length;
    saveConfig([...slots, newSlot]);
    setExpandedSlots(prev => new Set([...prev, newIndex.toString()]));
  }, [slots, saveConfig]);

  // Remove slot
  const removeSlot = useCallback((index: number) => {
    const newSlots = slots.filter((_, i) => i !== index);
    saveConfig(newSlots);
    // Re-index expandedSlots after removal
    setExpandedSlots(prev => {
      const next = new Set<string>();
      prev.forEach(idxStr => {
        const idx = parseInt(idxStr);
        if (idx < index) {
          next.add(idxStr);
        } else if (idx > index) {
          next.add((idx - 1).toString());
        }
        // idx === index is removed
      });
      return next;
    });
  }, [slots, saveConfig]);

  // Update slot
  const updateSlot = useCallback((index: number, updates: Partial<SlotConfig>) => {
    saveConfig(slots.map((s, i) => i === index ? { ...s, ...updates } : s));
  }, [slots, saveConfig]);

  // If the workflow is (or becomes) single_per_request, strip any stale mask config so
  // it can't double-bill at runtime. The mask UI is hidden in that mode, so a config
  // that was enabled under all_per_request and then switched would otherwise linger.
  // Guarded to run only when there is something to clear → no render loop.
  useEffect(() => {
    if (maskAllowed || !slotsWidget) return;
    const hasStaleMask = slots.some(s => s.enableMask || s.maskPayloadKey);
    if (!hasStaleMask) return;
    saveConfig(slots.map(s =>
      (s.enableMask || s.maskPayloadKey)
        ? { ...s, enableMask: undefined, maskPayloadKey: undefined }
        : s
    ));
  }, [maskAllowed, slots, slotsWidget, saveConfig]);

  // Toggle expand
  const toggleExpand = useCallback((index: number) => {
    const key = index.toString();
    setExpandedSlots(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newSlots = [...slots];
    const [removed] = newSlots.splice(draggedIndex, 1);
    newSlots.splice(targetIndex, 0, removed);
    saveConfig(newSlots);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Get media type icon
  const getMediaIcon = (type: MediaType) => {
    switch (type) {
      case 'image': return ICONS.IMAGE;
      case 'video': return ICONS.VIDEO;
      case 'audio': return ICONS.AUDIO;
      case 'model': return ICONS.CUBE_3D;
    }
  };

  // Get media type color
  const getMediaColor = (type: MediaType) => {
    switch (type) {
      case 'image': return 'blue';
      case 'video': return 'purple';
      case 'audio': return 'orange';
      case 'model': return 'cyan';
    }
  };

  // Render accepted formats checkboxes
  const renderFormatCheckboxes = (slot: SlotConfig, index: number) => {
    const allFormats = DEFAULT_FORMATS[slot.mediaType];
    return (
      <div className="mb-3">
        <label className="block text-xs text-gray-500 mb-2">
          {t.admin?.acceptedFormats || 'Accepted Formats'}
        </label>
        <div className="flex flex-wrap gap-2">
          {allFormats.map((fmt) => {
            const isChecked = !slot.acceptedFormats?.length || slot.acceptedFormats.includes(fmt);
            return (
              <label key={fmt} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    const currentFormats = slot.acceptedFormats?.length
                      ? [...slot.acceptedFormats]
                      : [...allFormats];
                    if (e.target.checked) {
                      if (!currentFormats.includes(fmt)) currentFormats.push(fmt);
                    } else {
                      const idx = currentFormats.indexOf(fmt);
                      if (idx >= 0 && currentFormats.length > 1) currentFormats.splice(idx, 1);
                    }
                    const isAllSelected = allFormats.every(f => currentFormats.includes(f));
                    updateSlot(index, {
                      acceptedFormats: isAllSelected ? undefined : currentFormats
                    });
                  }}
                  className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-800 text-green-500 accent-green-500"
                />
                <span className="text-xs text-gray-300 font-mono">{fmt}</span>
              </label>
            );
          })}
        </div>
        {slot.acceptedFormats?.length ? (
          <p className="text-[10px] text-gray-500 mt-1">
            {t.admin?.acceptedFormatsHint || 'Uncheck formats to restrict them. At least 1 required.'}
          </p>
        ) : (
          <p className="text-[10px] text-gray-500 mt-1">
            {t.admin?.allFormats || 'All formats'}
          </p>
        )}
      </div>
    );
  };

  // Render slot restrictions
  const renderRestrictions = (slot: SlotConfig, index: number) => {
    switch (slot.mediaType) {
      case 'image':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-700">
            <h5 className="text-xs font-medium text-gray-400 flex items-center gap-1">
              <Icon name={ICONS.IMAGE} size={14} className="text-blue-400" />
              {language === 'ru' ? 'Ограничения для изображений' : 'Image Restrictions'}
            </h5>
            {renderFormatCheckboxes(slot, index)}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. размер файла (MB)' : 'Max file size (MB)'}
                </label>
                <input
                  type="number"
                  value={slot.imageMaxFileSize || ''}
                  onChange={(e) => updateSlot(index, { imageMaxFileSize: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="20"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div></div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Мин. ширина (px)' : 'Min width (px)'}
                </label>
                <input
                  type="number"
                  value={slot.imageMinWidth || ''}
                  onChange={(e) => updateSlot(index, { imageMinWidth: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="512"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. ширина (px)' : 'Max width (px)'}
                </label>
                <input
                  type="number"
                  value={slot.imageMaxWidth || ''}
                  onChange={(e) => updateSlot(index, { imageMaxWidth: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="2048"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Мин. высота (px)' : 'Min height (px)'}
                </label>
                <input
                  type="number"
                  value={slot.imageMinHeight || ''}
                  onChange={(e) => updateSlot(index, { imageMinHeight: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="512"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. высота (px)' : 'Max height (px)'}
                </label>
                <input
                  type="number"
                  value={slot.imageMaxHeight || ''}
                  onChange={(e) => updateSlot(index, { imageMaxHeight: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="2048"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
            </div>

            {/* Image Processing: Target Dimensions & JPEG Conversion */}
            <div className="mt-4 pt-3 border-t border-gray-700/50">
              <h5 className="text-xs font-medium text-gray-400 flex items-center gap-1 mb-3">
                <Icon name={ICONS.SETTINGS} size={14} className="text-green-400" />
                {t.admin?.imageProcessingTitle || 'Image Processing'}
              </h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    {t.admin?.targetWidth || 'Target width (px)'}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={slot.targetWidth ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : undefined;
                      // Clear both if width is cleared and height was set alone (require both or neither)
                      if (!val && !slot.targetHeight) {
                        updateSlot(index, { targetWidth: undefined });
                      } else {
                        updateSlot(index, { targetWidth: val });
                      }
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="1280"
                    className={`w-full px-2 py-1.5 bg-gray-800 border rounded text-white text-sm ${
                      !slot.targetWidth && slot.targetHeight ? 'border-yellow-500' : 'border-gray-600'
                    }`}
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    {t.admin?.targetWidthDesc || 'Image will be center-cropped and resized to the specified width'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    {t.admin?.targetHeight || 'Target height (px)'}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={slot.targetHeight ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : undefined;
                      if (!val && !slot.targetWidth) {
                        updateSlot(index, { targetHeight: undefined });
                      } else {
                        updateSlot(index, { targetHeight: val });
                      }
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="720"
                    className={`w-full px-2 py-1.5 bg-gray-800 border rounded text-white text-sm ${
                      slot.targetWidth && !slot.targetHeight ? 'border-yellow-500' : 'border-gray-600'
                    }`}
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    {t.admin?.targetHeightDesc || 'Image will be center-cropped and resized to the specified height'}
                  </p>
                </div>
              </div>
              {/* Warning when only one dimension is set */}
              {((slot.targetWidth && !slot.targetHeight) || (!slot.targetWidth && slot.targetHeight)) && (
                <p className="text-[11px] text-yellow-400 mt-1">
                  {t.admin?.targetDimensionsPairWarning || 'Both width and height must be set for crop/resize to take effect'}
                </p>
              )}
              <div className="mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={slot.convertToJpeg || false}
                    onChange={(e) => updateSlot(index, { convertToJpeg: e.target.checked ? true : undefined })}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-green-500 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-300">
                    {t.admin?.convertToJpeg || 'Convert to JPEG (remove transparency)'}
                  </span>
                </label>
                <p className="text-[10px] text-gray-500 mt-1 ml-6">
                  {t.admin?.convertToJpegDesc || 'PNG with alpha channel will be converted to JPEG with white background'}
                </p>
              </div>
            </div>

            {/* Mask is gated on all_per_request — explain why it's unavailable.
                Only shown for the Big widget, which is the only one with runtime masking. */}
            {slotSupportsMask(slot) && maskSupportedByWidget && !maskAllowed && (
              <div className="mt-4 pt-3 border-t border-gray-700/50">
                <p className="text-[11px] text-amber-400/80">
                  {language === 'ru'
                    ? 'Редактор маски доступен только для режима «все файлы за запрос» (all_per_request).'
                    : 'Mask editor is only available for the all_per_request files mode.'}
                </p>
              </div>
            )}

            {/* Inpainting Mask Editor (Big widget only, single-image slots, all_per_request) */}
            {slotSupportsMask(slot) && maskSupportedByWidget && maskAllowed && (
              <div className="mt-4 pt-3 border-t border-gray-700/50">
                <h5 className="text-xs font-medium text-gray-400 flex items-center gap-1 mb-3">
                  <Icon name={ICONS.EDIT} size={14} className="text-pink-400" />
                  {t.maskEditor?.title || 'Mask Editor'}
                </h5>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={slot.enableMask || false}
                    onChange={(e) => updateSlot(index, {
                      enableMask: e.target.checked ? true : undefined,
                      // Default the key on enable so the common path works without
                      // a silent empty-key skip; drop it on disable to keep config clean.
                      ...(e.target.checked
                        ? (slot.maskPayloadKey?.trim() ? {} : { maskPayloadKey: 'mask_image' })
                        : { maskPayloadKey: undefined })
                    })}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-green-500 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-300">
                    {t.admin?.enableMask || 'Enable mask editor'}
                  </span>
                </label>
                <p className="text-[10px] text-gray-500 mt-1 ml-6">
                  {t.admin?.enableMaskDesc || 'Show a "Mask" button on the filled slot to paint an inpainting mask'}
                </p>

                {slot.enableMask && (
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">
                      {t.admin?.maskPayloadKey || 'Mask payload key'}
                    </label>
                    <input
                      type="text"
                      value={slot.maskPayloadKey || ''}
                      onChange={(e) => updateSlot(index, { maskPayloadKey: e.target.value || undefined })}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder={t.admin?.maskPayloadKeyPlaceholder || 'mask_image'}
                      className={`w-full px-2 py-1.5 bg-gray-800 border rounded text-white text-sm font-mono ${
                        maskPayloadKeyHasError(slot) || maskPayloadKeyCollides(slot, slots) ? 'border-red-500' : 'border-gray-600'
                      }`}
                    />
                    {maskPayloadKeyHasError(slot) && (
                      <p className="text-[11px] text-red-400 mt-1">
                        {t.admin?.maskPayloadKeyRequired || 'Mask payload key is required'}
                      </p>
                    )}
                    {!maskPayloadKeyHasError(slot) && maskPayloadKeyCollides(slot, slots) && (
                      <p className="text-[11px] text-red-400 mt-1">
                        {t.maskEditor?.keyCollision || 'Mask key collides with another request field — set a unique key'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-700">
            <h5 className="text-xs font-medium text-gray-400 flex items-center gap-1">
              <Icon name={ICONS.VIDEO} size={14} className="text-purple-400" />
              {language === 'ru' ? 'Ограничения для видео' : 'Video Restrictions'}
            </h5>
            {renderFormatCheckboxes(slot, index)}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. размер файла (MB)' : 'Max file size (MB)'}
                </label>
                <input
                  type="number"
                  value={slot.videoMaxFileSize || ''}
                  onChange={(e) => updateSlot(index, { videoMaxFileSize: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="100"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t.admin?.videoMinDuration || (language === 'ru' ? 'Мин. длительность (сек)' : 'Min duration (sec)')}
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={slot.videoMinDuration || ''}
                  onChange={(e) => updateSlot(index, { videoMinDuration: Number(e.target.value) || undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="4"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. длительность (сек)' : 'Max duration (sec)'}
                </label>
                <input
                  type="number"
                  value={slot.videoMaxDuration || ''}
                  onChange={(e) => updateSlot(index, { videoMaxDuration: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="60"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Мин. ширина (px)' : 'Min width (px)'}
                </label>
                <input
                  type="number"
                  value={slot.videoMinWidth || ''}
                  onChange={(e) => updateSlot(index, { videoMinWidth: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder=""
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. ширина (px)' : 'Max width (px)'}
                </label>
                <input
                  type="number"
                  value={slot.videoMaxWidth || ''}
                  onChange={(e) => updateSlot(index, { videoMaxWidth: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder=""
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Мин. высота (px)' : 'Min height (px)'}
                </label>
                <input
                  type="number"
                  value={slot.videoMinHeight || ''}
                  onChange={(e) => updateSlot(index, { videoMinHeight: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder=""
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. высота (px)' : 'Max height (px)'}
                </label>
                <input
                  type="number"
                  value={slot.videoMaxHeight || ''}
                  onChange={(e) => updateSlot(index, { videoMaxHeight: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder=""
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t.admin?.videoMinPixels || (language === 'ru' ? 'Мин. пикселей в кадре' : 'Min pixels per frame')}
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={slot.videoMinPixels || ''}
                  onChange={(e) => updateSlot(index, { videoMinPixels: Number(e.target.value) || undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="407696"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  {language === 'ru' ? 'ширина × высота, например 407696' : 'width × height, e.g. 407696'}
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t.admin?.videoMaxPixels || (language === 'ru' ? 'Макс. пикселей в кадре' : 'Max pixels per frame')}
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={slot.videoMaxPixels || ''}
                  onChange={(e) => updateSlot(index, { videoMaxPixels: Number(e.target.value) || undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="2073600"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  {language === 'ru' ? 'ширина × высота, например 2073600' : 'width × height, e.g. 2073600'}
                </p>
              </div>
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-700">
            <h5 className="text-xs font-medium text-gray-400 flex items-center gap-1">
              <Icon name={ICONS.AUDIO} size={14} className="text-orange-400" />
              {language === 'ru' ? 'Ограничения для аудио' : 'Audio Restrictions'}
            </h5>
            {renderFormatCheckboxes(slot, index)}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. размер файла (MB)' : 'Max file size (MB)'}
                </label>
                <input
                  type="number"
                  value={slot.audioMaxFileSize || ''}
                  onChange={(e) => updateSlot(index, { audioMaxFileSize: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="50"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {language === 'ru' ? 'Макс. длительность (сек)' : 'Max duration (sec)'}
                </label>
                <input
                  type="number"
                  value={slot.audioMaxDuration || ''}
                  onChange={(e) => updateSlot(index, { audioMaxDuration: e.target.value ? Number(e.target.value) : undefined })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="300"
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                />
              </div>
            </div>
          </div>
        );

      case 'model':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-700">
            <h5 className="text-xs font-medium text-gray-400 flex items-center gap-1">
              <Icon name={ICONS.CUBE_3D} size={14} className="text-cyan-400" />
              {t.admin?.modelRestrictions || '3D Model Restrictions'}
            </h5>
            {renderFormatCheckboxes(slot, index)}
            <div className="max-w-xs">
              <label className="block text-xs text-gray-500 mb-1">
                {t.admin?.modelMaxFileSize || 'Max file size (MB)'}
              </label>
              <input
                type="number"
                min={1}
                value={slot.modelMaxFileSize || ''}
                onChange={(e) => updateSlot(index, { modelMaxFileSize: e.target.value ? Number(e.target.value) : undefined })}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="100"
                className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                {t.admin?.modelMaxFileSizeHint || 'Maximum allowed size for each uploaded 3D model.'}
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Icon name={ICONS.UPLOAD} size={20} className="text-green-400" />
          {t.admin?.slotsInputMediaSettings || 'Slots Input Media Settings'}
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {t.admin?.slotsInputMediaSettingsDesc || 'Configure media upload slots with individual restrictions for each slot.'}
        </p>
      </section>

      {/* No Widget Warning */}
      {!hasWidget && (
        <section className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name={ICONS.INFO} size={20} className="text-yellow-400 mt-0.5" />
            <div>
              <p className="text-yellow-300 font-medium">
                {t.admin?.noSlotsInputMediaWidget || 'Widget not enabled'}
              </p>
              <p className="text-sm text-yellow-400/80 mt-1">
                {t.admin?.noSlotsInputMediaWidgetDesc || 'Enable "Slots Input Media" widget in the Widgets section to configure its settings.'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Slots Configuration */}
      {hasWidget && (
        <>
          {/* Add Slot Button */}
          <button
            type="button"
            onClick={addSlot}
            className="w-full px-4 py-3 border-2 border-dashed border-gray-600 hover:border-green-500 rounded-xl text-gray-400 hover:text-green-400 transition-colors flex items-center justify-center gap-2"
          >
            <Icon name={ICONS.PLUS} size={20} />
            {language === 'ru' ? 'Добавить слот' : 'Add Slot'}
          </button>

          {/* Slots List */}
          <div className="space-y-3">
            {slots.map((slot, index) => {
              const isExpanded = expandedSlots.has(index.toString());
              const color = getMediaColor(slot.mediaType);

              return (
                <div
                  key={index}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`
                    bg-gray-800/50 border rounded-xl overflow-hidden transition-all
                    ${draggedIndex === index ? 'opacity-50 border-green-500' : `border-${color}-500/30 hover:border-${color}-500/50`}
                  `}
                >
                  {/* Slot Header */}
                  <div
                    className="w-full flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-700/30 transition-colors"
                    onClick={() => toggleExpand(index)}
                  >
                    {/* Drag Handle */}
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-grab text-gray-500 hover:text-gray-300"
                    >
                      <Icon name={ICONS.GRIP} size={16} />
                    </div>

                    {/* Media Type Icon */}
                    <Icon
                      name={getMediaIcon(slot.mediaType)}
                      size={18}
                      className={`text-${color}-400`}
                    />

                    {/* Slot Name */}
                    <span className="flex-1 text-white font-medium truncate text-left">
                      {(language === 'ru' ? slot.nameRu : slot.nameEn) || (language === 'ru' ? `Слот ${index + 1}` : `Slot ${index + 1}`)}
                    </span>

                    {/* Required Badge */}
                    {slot.required && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                        {language === 'ru' ? 'Обязательный' : 'Required'}
                      </span>
                    )}

                    {/* Expand/Collapse */}
                    <Icon
                      name={isExpanded ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN}
                      size={18}
                      className="text-gray-400"
                    />

                    {/* Delete Button */}
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSlot(index);
                      }}
                      className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <Icon name={ICONS.TRASH} size={16} />
                    </span>
                  </div>

                  {/* Slot Settings (Expanded) */}
                  {isExpanded && (
                    <div className="p-4 pt-0 border-t border-gray-700 space-y-4">
                      {/* Names */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                            <span className="text-base">🇷🇺</span>
                            {language === 'ru' ? 'Название (рус)' : 'Name (Russian)'}
                          </label>
                          <input
                            type="text"
                            value={slot.nameRu}
                            onChange={(e) => updateSlot(index, { nameRu: e.target.value })}
                            onKeyDown={(e) => e.stopPropagation()}
                            placeholder={language === 'ru' ? 'Стартовый кадр' : 'Start Frame'}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                            <span className="text-base">🇬🇧</span>
                            {language === 'ru' ? 'Название (англ)' : 'Name (English)'}
                          </label>
                          <input
                            type="text"
                            value={slot.nameEn}
                            onChange={(e) => updateSlot(index, { nameEn: e.target.value })}
                            onKeyDown={(e) => e.stopPropagation()}
                            placeholder="Start Frame"
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500"
                          />
                        </div>
                      </div>

                      {/* Slot ID for API */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">
                          Slot ID ({language === 'ru' ? 'для API' : 'for API'})
                        </label>
                        <input
                          type="text"
                          value={slot.slot_id}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                            if (slotIdCollidesWithReservedMediaKey(value)) return;
                            updateSlot(index, { slot_id: value });
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="start_frame, input_video..."
                          aria-invalid={slotIdCollidesWithReservedMediaKey(slot.slot_id)}
                          className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white text-sm font-mono focus:border-green-500 ${
                            slotIdCollidesWithReservedMediaKey(slot.slot_id)
                              ? 'border-red-500'
                              : 'border-gray-600'
                          }`}
                        />
                        <p className="text-[10px] text-gray-500 mt-1">
                          {language === 'ru'
                            ? 'Только латиница, цифры и _. Используется как ключ в API запросе.'
                            : 'Only letters, numbers and _. Used as key in API request.'}
                        </p>
                        {slotIdCollidesWithReservedMediaKey(slot.slot_id) && (
                          <p className="text-[10px] text-red-400 mt-1">
                            {language === 'ru'
                              ? 'Этот ключ зарезервирован для стандартных медиафайлов.'
                              : 'This key is reserved for standard media inputs.'}
                          </p>
                        )}
                      </div>

                      {/* Type & Settings Row */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {/* Media Type */}
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            {language === 'ru' ? 'Тип медиа' : 'Media Type'}
                          </label>
                          <select
                            value={slot.mediaType}
                            onChange={(e) => {
                              const newType = e.target.value as MediaType;
                              saveConfig(slots.map((currentSlot, slotIndex) =>
                                slotIndex === index ? changeSlotMediaType(currentSlot, newType) : currentSlot
                              ));
                            }}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500"
                          >
                            <option value="image">{language === 'ru' ? 'Изображение' : 'Image'}</option>
                            <option value="video">{language === 'ru' ? 'Видео' : 'Video'}</option>
                            <option value="audio">{language === 'ru' ? 'Аудио' : 'Audio'}</option>
                            <option value="model" disabled={!modelSupportedByWidget}>
                              {t.admin?.modelMediaType || '3D Model'}
                            </option>
                          </select>
                          {!modelSupportedByWidget && slot.mediaType === 'model' && (
                            <p className="mt-1 text-[10px] text-amber-400">
                              {t.widgets?.slotsInputMedia?.modelUnsupportedInSmall
                                || '3D models are supported only by the large slots media widget.'}
                            </p>
                          )}
                        </div>

                        {/* Max Count */}
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            {language === 'ru' ? 'Макс. файлов' : 'Max Files'}
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={30}
                            value={slot.maxCount}
                            onChange={(e) => {
                              const newCount = Math.max(1, Math.min(30, Number(e.target.value)));
                              // Mask is allowed on multi-image slots (binds to the first image),
                              // so raising the count no longer clears mask config.
                              updateSlot(index, { maxCount: newCount });
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500"
                          />
                        </div>

                        {/* Required Checkbox */}
                        <div className="flex items-end pb-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={slot.required}
                              onChange={(e) => updateSlot(index, { required: e.target.checked })}
                              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-green-500 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-300">
                              {language === 'ru' ? 'Обязательный' : 'Required'}
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Media Type Restrictions */}
                      {renderRestrictions(slot, index)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {slots.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Icon name={ICONS.UPLOAD} size={32} className="mx-auto mb-2 opacity-50" />
              <p>{language === 'ru' ? 'Нет настроенных слотов' : 'No slots configured'}</p>
              <p className="text-sm mt-1">{language === 'ru' ? 'Нажмите "Добавить слот" чтобы начать' : 'Click "Add Slot" to get started'}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
